import { buildStationRows, rowHeaderBlocks, rowHierarchyDepth, rowGroupStarts } from './shared/groupRows'
import { stationLabelLines } from './shared/StationLabelFields'

// Styl jako MlyGrid — tenké čáry, gray-400 jen kolem hlavičky a sticky sloupce.
const th = 'border-b border-b-gray-400 border-r border-r-gray-200 px-1 py-1 text-xs font-normal text-center bg-gray-50 whitespace-nowrap'

// Záložka "Detail" produktu grp-jevy — 1 vybraný jev, řádky = stanice, sloupce = dny v měsíci,
// buňka = ikona jevu, když daný den na té stanici nastal.
// Viz doc/plans/station-group-monthly-yearly-phenomena.md sekce 5b.
export function GroupJevyDetailGrid({ nodes, stationsByGhId, dayMap, dayKeys, phenoDef, labelFields, orientation = 'normal' }) {
  const rows = buildStationRows(nodes, stationsByGhId)
  const depth = rowHierarchyDepth(nodes)
  const headerCols = Array.from({ length: depth }, (_, d) => rowHeaderBlocks(rows, d))

  const groupStarts = depth >= 1 ? rowGroupStarts(rows, 0) : new Set()
  const topRule = ri => (groupStarts.has(ri) ? 'border-t border-t-gray-400' : '')

  if (orientation === 'transposed') {
    const leftRule = ci => (groupStarts.has(ci) ? 'border-l border-l-gray-400' : '')
    return (
      <div className="h-full overflow-hidden border border-gray-200">
        <div className="h-full overflow-auto">
          <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs table-fixed">
            <colgroup>
              <col className="w-12" />
              {rows.map(r => <col key={r.rowKey} className="w-16" />)}
            </colgroup>
            <thead className="sticky top-0 z-20">
              {headerCols.map((blocks, d) => (
                <tr key={`h${d}`}>
                  {d === 0 && (
                    <th rowSpan={depth + 1} className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-2 py-1 text-left align-bottom text-gray-600 font-semibold whitespace-nowrap">
                      Den
                    </th>
                  )}
                  {blocks.map(block => (
                    <th key={`${d}-${block.start}`} colSpan={block.span} className={`${th} border-t-2 border-t-gray-400 text-gray-500 ${leftRule(block.start)}`}>
                      {block.label}
                    </th>
                  ))}
                </tr>
              ))}
              <tr>
                {depth === 0 && (
                  <th className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-2 py-1 text-left text-gray-600 font-semibold whitespace-nowrap">
                    Den
                  </th>
                )}
                {rows.map((row, ci) => {
                  const station = stationsByGhId.get(row.gh)
                  return (
                    <th key={row.rowKey} className={`${th} text-gray-600 font-medium ${depth === 0 ? 'border-t-2 border-t-gray-400' : ''} ${leftRule(ci)}`}>
                      {stationLabelLines(station, labelFields, row.gh).map((line, li) => (
                        <div key={li} className={li > 0 ? 'text-[10px] text-gray-400 font-normal' : ''}>{line}</div>
                      ))}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {dayKeys.map(key => {
                const d = new Date(key + 'T00:00:00Z')
                return (
                  <tr key={key} className="group hover:bg-slate-50">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-2 py-0.5 text-center font-semibold text-gray-600 whitespace-nowrap">
                      {d.getUTCDate()}.
                    </td>
                    {rows.map((row, ci) => {
                      const has = dayMap.get(row.gh)?.get(key)?.has(phenoDef?.code)
                      return (
                        <td key={row.rowKey} className={`border-b border-r border-gray-200 text-center py-1 ${leftRule(ci)}`}>
                          {has && phenoDef?.svg && (
                            <span className="inline-block w-4 h-4" dangerouslySetInnerHTML={{ __html: phenoDef.svg }} title={phenoDef.name} />
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={depth + 1 + dayKeys.length} className="px-4 py-6 text-center text-gray-400">
                    Žádné stanice
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs table-fixed">
          <colgroup>
            {Array.from({ length: depth }, (_, i) => <col key={`h${i}`} className="w-28" />)}
            <col className="w-40" />
            {dayKeys.map(key => <col key={key} className="w-7" />)}
          </colgroup>
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                colSpan={depth + 1}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold whitespace-nowrap"
              >
                Stanice
              </th>
              {dayKeys.map(key => {
                const d = new Date(key + 'T00:00:00Z')
                return (
                  <th key={key} className={`${th} border-t-2 border-t-gray-400 text-gray-500`}>{d.getUTCDate()}.</th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const station = stationsByGhId.get(row.gh)
              const perDay = dayMap.get(row.gh) ?? new Map()
              return (
                <tr key={row.rowKey} className="group hover:bg-slate-50">
                  {headerCols.map((blocks, d) => {
                    const block = blocks.find(b => b.start === ri)
                    if (!block) return null
                    return (
                      <td
                        key={d}
                        rowSpan={block.span}
                        className={`sticky z-10 bg-gray-100 border-b border-r border-gray-200 px-2 py-0.5 text-left align-top text-[11px] font-medium text-gray-600 whitespace-nowrap ${d === 0 ? 'border-l-2 border-l-gray-400' : ''} ${topRule(ri)}`}
                        style={{ left: `${d * 7}rem` }}
                      >
                        {block.label}
                      </td>
                    )
                  })}
                  <td
                    className={`sticky z-10 bg-white group-hover:bg-slate-50 border-b border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap ${depth === 0 ? 'border-l-2 border-l-gray-400' : 'border-l border-l-gray-400'} ${topRule(ri)}`}
                    style={{ left: `${depth * 7}rem` }}
                  >
                    {stationLabelLines(station, labelFields, row.gh).map((line, li) => (
                      <div key={li} className={li > 0 ? 'text-[10px] text-gray-400' : ''}>{line}</div>
                    ))}
                  </td>
                  {dayKeys.map(key => {
                    const has = perDay.get(key)?.has(phenoDef?.code)
                    return (
                      <td key={key} className={`border-b border-r border-gray-200 text-center py-1 ${topRule(ri)}`}>
                        {has && phenoDef?.svg && (
                          <span className="inline-block w-4 h-4" dangerouslySetInnerHTML={{ __html: phenoDef.svg }} title={phenoDef.name} />
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={depth + 1 + dayKeys.length} className="px-4 py-6 text-center text-gray-400">
                  Žádné stanice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
